// Settings Dialog - Global App Settings
import { useEffect } from 'preact/hooks'
import { basis, domain } from '../../state/globalSignals.js'
import { closeSettings, isSettingsOpen, setThemeMode } from '../../state/themeStore.js'

export default function SettingsDialog() {
  // Close on ESC key
  useEffect(() => {
    if (!isSettingsOpen.value) return
    
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        closeSettings()
      }
    }
    
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isSettingsOpen.value])
  
  if (!isSettingsOpen.value) return null
  
  const handleClearCache = () => {
    if (window.__BATEMO_API_CACHE__?.clear) {
      window.__BATEMO_API_CACHE__.clear()
      alert('Cache cleared successfully')
    }
  }
  
  const handleResetSettings = () => {
    if (confirm('Reset all settings to defaults?')) {
      setThemeMode('dark')
      basis.value = 'abs'
      domain.value = 'energy-power'
      alert('Settings reset to defaults')
    }
  }
  
  return (
    <>
      {/* Backdrop */}
      <div 
        className="settings-backdrop"
        onClick={closeSettings}
      />
      
      {/* Dialog */}
      <div className="settings-dialog">
        {/* Header */}
        <div className="settings-header">
          <h2 className="settings-title">Settings</h2>
          <button 
            className="settings-close-btn"
            onClick={closeSettings}
            aria-label="Close settings"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>
        
        {/* Content */}
        <div className="settings-content">
          
          {/* Data Section */}
          <section className="settings-section">
            <h3 className="settings-section-title">Data & Performance</h3>
            
            <div className="settings-option">
              <label className="settings-label">
                <span className="settings-label-text">Cache Management</span>
                <span className="settings-label-desc">Clear cached cell data (8h lifetime)</span>
              </label>
              <div className="settings-control">
                <button className="settings-action-btn" onClick={handleClearCache}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <polyline points="1 4 1 10 7 10"/>
                    <path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/>
                  </svg>
                  Clear Cache
                </button>
              </div>
            </div>
            
            <div className="settings-option">
              <label className="settings-label">
                <span className="settings-label-text">Default Basis</span>
                <span className="settings-label-desc">Preferred data normalization</span>
              </label>
              <div className="settings-control">
                <select 
                  className="settings-select"
                  value={basis.value}
                  onChange={(e) => basis.value = e.target.value}
                >
                  <option value="abs">Absolute</option>
                  <option value="grav">Gravimetric (per kg)</option>
                  <option value="vol">Volumetric (per L)</option>
                </select>
              </div>
            </div>
            
            <div className="settings-option">
              <label className="settings-label">
                <span className="settings-label-text">Default Domain</span>
                <span className="settings-label-desc">Preferred data domain</span>
              </label>
              <div className="settings-control">
                <select 
                  className="settings-select"
                  value={domain.value}
                  onChange={(e) => domain.value = e.target.value}
                >
                  <option value="energy-power">Energy & Power</option>
                  <option value="capacity-current">Capacity & Current</option>
                </select>
              </div>
            </div>
          </section>
          
          {/* Advanced Section */}
          <section className="settings-section">
            <h3 className="settings-section-title">Advanced</h3>
            
            <div className="settings-option">
              <label className="settings-label">
                <span className="settings-label-text">Reset to Defaults</span>
                <span className="settings-label-desc">Restore all default settings</span>
              </label>
              <div className="settings-control">
                <button className="settings-action-btn settings-action-btn--danger" onClick={handleResetSettings}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 6h18M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/>
                  </svg>
                  Reset
                </button>
              </div>
            </div>
            
            <div className="settings-option">
              <label className="settings-label">
                <span className="settings-label-text">Version Info</span>
                <span className="settings-label-desc">Batemo Insights v2.1.0</span>
              </label>
              <div className="settings-control">
                <span className="settings-info-text">Build: 2025-11-18</span>
              </div>
            </div>
          </section>
          
        </div>
        
        {/* Footer */}
        <div className="settings-footer">
          <button className="settings-footer-btn" onClick={closeSettings}>
            Close
          </button>
        </div>
      </div>
    </>
  )
}
